import arrowUpIcon from "@assets/images/arrow-up-white.svg";
import folderIcon from "@assets/images/folder-open.svg";
import infoIcon from "@assets/images/info.svg";
import warningIcon from "@assets/images/warning-red.svg";
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography
} from "@material-ui/core";
import BuildIcon from "@material-ui/icons/Build";
import { useUser } from "@saleor/auth";
import { useUserPermissions } from "@saleor/auth/hooks/useUserPermissions";
import MyTypography from "@saleor/components/MyTypography";
import { PermissionEnum, usePoUploadFileMutation } from "@saleor/graphql";
import useNavigator from "@saleor/hooks/useNavigator";
import { poUploadSettingsUrl } from "@saleor/poUpload/urls";
import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import FileLine from "src/alternatedMaterial/components/UploadFile/FileLine";

import DialogErrorLine, { IError, IRefDialog } from "./DialogErrorLine";
import DialogSuccess, { IRefDialogSuccess } from "./DialogSuccess";
import useStyle from "./styles";

interface IFile {
  size: number;
  type: string;
  path: string;
  name: string;
}

interface IUploadFile {
  onUploadComplete?: () => void;
}

export default ({ onUploadComplete }: IUploadFile) => {
  const classes = useStyle();
  const intl = useIntl();
  const navigate = useNavigator();
  const permissions = useUserPermissions();
  const { company } = useUser();

  const hasMaintainPOPermission = permissions?.find(
    perm => perm.code === PermissionEnum.MAINTAIN_PO_UPLOAD
  );

  const {
    getRootProps,
    getInputProps,
    acceptedFiles,
    fileRejections
  } = useDropzone({
    multiple: false,
    accept: ".txt",
    maxSize: 5242880 // 5MB
  });

  const [file, setFile] = useState<IFile>();

  const dialogErrorRef = useRef<IRefDialog | null>(null);
  const dialogSuccessRef = useRef<null | IRefDialogSuccess>(null);
  const [errorLines, setErrorLines] = useState<IError[] | null>();
  const [errorUploadMessage, setErrorUploadMessage] = useState("");

  const onRemove = () => {
    setFile(null);
    setErrorLines(null);
  };

  const [uploadFile, uploadState] = usePoUploadFileMutation({
    onCompleted: data => {
      onUploadComplete();
      if (!!data?.uploadPo?.status) {
        dialogSuccessRef.current.onOpen(file?.name);
        setFile(null);
        setErrorLines(null);
      } else {
        const errors = data?.uploadPo?.errors;
        setErrorLines(errors);
        dialogErrorRef.current.onOpen(errors);
      }
    }
  });

  const onExplainError = () => {
    dialogErrorRef.current.onOpen(errorLines);
  };

  const onFileReady = () => {
    let result: any;
    if (acceptedFiles.length > 0) {
      result = acceptedFiles[0];
    } else if (fileRejections.length > 0) {
      result = fileRejections[0].file;
    }
    setErrorLines(null);
    setFile(result);
  };

  useEffect(() => {
    onFileReady();
  }, [acceptedFiles, fileRejections]);

  const isError = useMemo(() => {
    if (!!fileRejections[0]?.errors && file) {
      const errCode = fileRejections[0]?.errors[0]?.code;

      switch (errCode) {
        case "file-invalid-type":
          setErrorUploadMessage(
            "รองรับไฟล์สกุล .txt เท่านั้น,\nกรุณาลองใหม่อีกครั้ง"
          );
          break;
        case "file-too-large":
          setErrorUploadMessage(
            "รองรับไฟล์ขนาดไม่เกิน 5MB\nกรุณาอัพโหลดใหม่อีกครั้ง"
          );
          break;
        default:
          setErrorUploadMessage(fileRejections[0]?.errors[0]?.message);
      }

      return true;
    }

    setErrorUploadMessage("");
    return false;
  }, [fileRejections, file]);

  const canUpload = !isError && !!file;

  const onUpload = () => {
    if (canUpload) {
      uploadFile({ variables: { file, soldToCode: company } });
    }
  };

  const body = (
    <Box display={"flex"} flexDirection="column" flex={1}>
      <section className={classes.uploadBox}>
        <Box
          {...getRootProps({ className: "dropzone" })}
          flexDirection="column"
          alignItems={"center"}
          height="100%"
          justifyContent="center"
          display={"flex"}
        >
          <input {...getInputProps()} data-testid="file-input" />
          <SVG src={folderIcon} />
          <Typography className={classes.dragTitle}>
            Drag & Drop or
            <Typography component={"u"} className={`${classes.browse} `}>
              Browse
            </Typography>
            your file
          </Typography>
          <Typography className={classes.supportTitle}>
            File Supported : txt
          </Typography>
        </Box>
      </section>
      <FileLine path={file?.path} onRemove={onRemove} />
    </Box>
  );

  const BottomMessage = () => {
    const infoMessage = intl.formatMessage({
      defaultMessage: "รองรับไฟล์สกุล .txt เท่านั้น",
      description: "infoMessage"
    });

    const errorLineMessage = intl.formatMessage({
      defaultMessage: "อัพโหลดไฟล์ไม่สำเร็จ",
      description: "errorLineMessage"
    });

    const explainMessage = intl.formatMessage({
      defaultMessage: "ดูข้อมูลเพิ่มเติม",
      description: "explainMessage"
    });
    const isShowWarningIcon = isError || errorLines;

    return (
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems="center"
      >
        <Box
          alignItems={isError ? "flex-start" : "center"}
          display="flex"
          justifyContent={"center"}
        >
          <SVG
            src={isShowWarningIcon ? warningIcon : infoIcon}
            className={classes.infoIcon}
            style={{ marginTop: isError ? 4 : 0 }}
          />
          {errorLines && (
            <React.Fragment>
              <Typography className={classes.note}>
                {errorLineMessage} &nbsp;
              </Typography>
              <Typography
                onClick={onExplainError}
                component={"u"}
                style={{ fontWeight: 700 }}
                className={classes.note}
              >
                {explainMessage}
              </Typography>
            </React.Fragment>
          )}
          {!errorLines && (
            <Typography
              className={classes.note}
              style={{
                color: isError ? "#E41B17" : "#555555",
                whiteSpace: "pre-line"
              }}
            >
              {isError ? errorUploadMessage : infoMessage}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      className={classes.container}
      display={"flex"}
      flexDirection={"column"}
    >
      <div className={classes.titleContainer}>
        <MyTypography variant="subtitle1" className={classes.title}>
          Upload : PO File
        </MyTypography>
        {hasMaintainPOPermission && (
          <Tooltip
            title="Adjust Mapping"
            placement="top-end"
            arrow
            className={classes.adjustIconToolTips}
            onClick={() => navigate(poUploadSettingsUrl)}
          >
            <BuildIcon className={classes.adjustIcon} />
          </Tooltip>
        )}
      </div>
      {body}
      <Box display={"flex"} justifyContent={"space-between"} marginTop={"24px"}>
        <BottomMessage />
        <Button
          variant="contained"
          color="secondary"
          disabled={!canUpload || !!errorLines?.length || uploadState.loading}
          className={classNames(classes.button, "defaultB")}
          onClick={onUpload}
        >
          {uploadState.loading ? (
            <CircularProgress size={16} />
          ) : (
            <SVG src={arrowUpIcon} />
          )}

          <MyTypography variant="body2" className={classes.buttonTitle}>
            {intl.formatMessage({
              defaultMessage: "Upload File"
            })}
          </MyTypography>
        </Button>
      </Box>
      <DialogErrorLine ref={dialogErrorRef} />
      <DialogSuccess ref={dialogSuccessRef} />
    </Box>
  );
};
